import React, { forwardRef, useRef } from "react";
import {
  Chart,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useMediaQuery } from "@mui/material";
import "chartjs-adapter-date-fns";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";

Chart.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin,
  annotationPlugin,
);

const MonitoringChart = forwardRef(
  ({ measurements, lowAlarm, highAlarm, unit = "", label = "" }, ref) => {
    const chartRef = useRef();
    const isNarrow = useMediaQuery("(max-width:600px)");

    const dataPoints = (
      Array.isArray(measurements)
        ? measurements
        : Object.values(measurements ?? {})
    )
      .map((m) => ({
        x: new Date(m.time),
        y: Number(m.value),
        w: Number(m.dt) > 0 ? Number(m.dt) : 1,
      }))
      .sort((a, b) => a.x - b.x);

    // weighted by span: a plain mean of derived LoRa flow reads 8.1% low
    const weightedAvg = (points) => {
      let sum = 0;
      let weight = 0;
      for (const p of points) {
        sum += p.y * p.w;
        weight += p.w;
      }
      return weight > 0 ? sum / weight : 0;
    };

    const avgValue = weightedAvg(dataPoints);

    const qmin = lowAlarm ?? null;
    const qmax = highAlarm ?? null;

    const segmentColor = (ctx) => {
      const y = ctx.p1.parsed.y;
      if (qmax !== null && y > qmax) return "rgba(255, 0, 0, 0.8)";
      if (qmin !== null && y < qmin) return "rgba(255, 140, 0, 0.8)";
      return "#21538f";
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      elements: {
        point: { radius: 0 },
        line: { tension: 0.3, borderWidth: 2 },
      },
      interaction: {
        mode: "nearest",
        intersect: false,
        axis: "x",
      },
      scales: {
        x: {
          type: "time",
          time: {
            unit: "day",
            displayFormats: {
              hour: "HH:mm",
              day: "dd MMM",
              week: "dd MMM",
            },
            tooltipFormat: "dd MMM HH:mm",
          },
          ticks: {
            source: "auto",
            autoSkip: true,
            maxRotation: 0,
            font: { size: isNarrow ? 10 : 12 },
            maxTicksLimit: isNarrow ? 5 : undefined,
          },
          grid: { display: false },
        },
        y: {
          beginAtZero: false,
          grace: '15%',
          title: {
            display: !isNarrow,
            text: unit,
            font: { size: 14, weight: "500" },
            padding: { bottom: 20 },
          },
          ticks: {
            font: { size: isNarrow ? 10 : 12 },
            maxTicksLimit: isNarrow ? 6 : undefined,
          },
          afterFit: (scale) => {
            if (isNarrow) scale.width = Math.min(scale.width, 34);
          },
          grid: { color: "rgba(0,0,0,0.05)" },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          mode: "nearest",
          intersect: false,
          callbacks: {
            label: (ctx) => `${label}: ${ctx.parsed.y.toFixed(3)} ${unit}`,
            title: (ctx) => {
              const format = (ms) =>
                new Date(ms).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                });
              const start = ctx[0].parsed.x;
              const seconds = ctx[0].raw.w;
              return seconds > 60
                ? `${format(start)} – ${format(start + seconds * 1000)}`
                : format(start);
            },
          },
        },
        annotation: {
          annotations: {
            avgLine: {
              type: "line",
              yMin: avgValue,
              yMax: avgValue,
              borderColor: "rgba(100, 100, 100, 0.8)",
              borderWidth: 1,
              borderDash: [15, 15],
              label: {
                display: true,
                content: `Qsr: ${avgValue.toFixed(3)}`,
                enabled: true,
                position: "start",

                color: "rgba(160, 160, 160, 0.9)",
                font: { weight: "bold" },
              },
            },
            ...(qmax !== null && {
              qmaxLine: {
                type: "line",
                yMin: qmax,
                yMax: qmax,
                borderColor: "rgba(255, 0, 0, 0.7)",
                borderWidth: 1,
                borderDash: [15, 15],
              },
            }),
            ...(qmin !== null && {
              qminLine: {
                type: "line",
                yMin: qmin,
                yMax: qmin,
                borderColor: "rgba(255, 140, 0, 0.7)",
                borderWidth: 1,
                borderDash: [15, 15],
              },
            }),
          },
        },
        zoom: {
          limits: { x: { minRange: 1000 * 60 * 15 } },
          pan: { enabled: true, mode: "x", threshold: 5 },
          zoom: {
            wheel: { enabled: true, speed: 0.05 },
            pinch: { enabled: true },
            mode: "x",
            drag: { enabled: false },
            onZoom: ({ chart }) => {
              const xScale = chart.scales.x;
              const range = xScale.max - xScale.min;
              const oneDay = 24 * 60 * 60 * 1000;

              if (range < oneDay * 2) chart.options.scales.x.time.unit = "hour";
              else if (range < oneDay * 14)
                chart.options.scales.x.time.unit = "day";
              else chart.options.scales.x.time.unit = "week";

              const visibleData = dataPoints.filter(
                (p) => p.x >= xScale.min && p.x <= xScale.max,
              );
              const visibleAvg =
                visibleData.length > 0 ? weightedAvg(visibleData) : avgValue;

              chart.options.plugins.annotation.annotations.avgLine.yMin =
                visibleAvg;
              chart.options.plugins.annotation.annotations.avgLine.yMax =
                visibleAvg;
              chart.options.plugins.annotation.annotations.avgLine.label.content = `Qsr: ${visibleAvg.toFixed(
                3,
              )}`;

              chart.update("none");
            },
          },
        },
      },
    };

    const data = {
      datasets: [
        {
          data: dataPoints,
          fill: false,
          pointRadius: 0,
          borderWidth: 2,
          borderColor: "#21538f",
          segment: { borderColor: segmentColor },
        },
      ],
    };

    return <Line ref={ref || chartRef} data={data} options={options} />;
  },
);

export default MonitoringChart;
